import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  formatBytes,
  formatDate,
  type AdminSummary,
  type AdminUser,
  type Role,
} from './api';
import { Auditoria, etiquetaAccion } from './Auditoria';
import { Campo, Confirmar, MenuAcciones, Modal } from './ui';

const ROLES: Array<{ id: Role; nombre: string; detalle: string }> = [
  { id: 'user', nombre: 'Usuario', detalle: 'Solo ve lo que le comparten' },
  { id: 'operator', nombre: 'Operador', detalle: 'Administra su propia carpeta' },
  { id: 'admin', nombre: 'Administrador', detalle: 'Además gestiona usuarios' },
];

const GB = 1073741824;

export function Admin({ usuarioActual }: { usuarioActual: number }) {
  const [resumen, setResumen] = useState<AdminSummary | null>(null);
  const [usuarios, setUsuarios] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<AdminUser | null>(null);
  const [creando, setCreando] = useState(false);
  const [porEliminar, setPorEliminar] = useState<{ u: AdminUser; purgar: boolean } | null>(null);
  const [pestania, setPestania] = useState<'usuarios' | 'auditoria'>('usuarios');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [s, u] = await Promise.all([api.adminSummary(), api.adminUsers()]);
      setResumen(s);
      setUsuarios(u);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar el panel.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Dos acciones separadas en vez de un confirm con dos sentidos: borrar los
  // archivos de alguien no puede quedar escondido detrás de "aceptar o cancelar".
  async function eliminar(u: AdminUser, purgarArchivos: boolean) {
    setPorEliminar(null);
    try {
      await api.adminDeleteUser(u.id, purgarArchivos);
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos eliminar el usuario.');
    }
  }

  async function alternarActivo(u: AdminUser) {
    try {
      await api.adminUpdateUser(u.id, { active: !u.active });
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cambiar el estado.');
    }
  }

  if (cargando) return <p className="vacio">Cargando…</p>;

  return (
    <section className="admin">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {resumen && (
        <div className="tarjetas-resumen">
          <Tarjeta titulo="Usuarios activos" valor={String(resumen.usuarios.activos)}>
            {Object.entries(resumen.usuarios.porRol).map(([rol, n]) => (
              <span key={rol} className="chip">
                {n} {ROLES.find((r) => r.id === rol)?.nombre ?? rol}
              </span>
            ))}
          </Tarjeta>

          <Tarjeta
            titulo="Almacenamiento usado"
            valor={formatBytes(resumen.almacenamiento.usadoBytes)}
          >
            <span className="sub">de {formatBytes(resumen.almacenamiento.asignadoBytes)} asignados</span>
          </Tarjeta>

          <Tarjeta titulo="Compartidos" valor={String(resumen.compartidos)} />
          <Tarjeta titulo="Links activos" valor={String(resumen.linksActivos)} />
        </div>
      )}

      <div className="subpestanias pestanias-admin" role="tablist">
        <button
          role="tab"
          aria-selected={pestania === 'usuarios'}
          className={pestania === 'usuarios' ? 'activa' : ''}
          onClick={() => setPestania('usuarios')}
        >
          Usuarios ({usuarios.length})
        </button>
        <button
          role="tab"
          aria-selected={pestania === 'auditoria'}
          className={pestania === 'auditoria' ? 'activa' : ''}
          onClick={() => setPestania('auditoria')}
        >
          Auditoría
        </button>
      </div>

      {pestania === 'auditoria' && <Auditoria usuarios={usuarios} />}

      {pestania === 'usuarios' && (
        <>
          <div className="seccion-encabezado">
            <h2>Usuarios</h2>
            <button onClick={() => setCreando(true)}>Nuevo usuario</button>
          </div>

          <div className="tabla-scroll">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th className="col-fecha">Correo</th>
                  <th>Rol</th>
                  <th className="col-tamanio">Uso</th>
                  <th className="col-tamanio">Estado</th>
                  <th className="col-acciones" />
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} className={u.active ? '' : 'fila-inactiva'}>
                    <td>{u.displayName}</td>
                    <td className="mono">{u.username}</td>
                    <td className="col-fecha">{u.email ?? '—'}</td>
                    <td>{ROLES.find((r) => r.id === u.role)?.nombre ?? u.role}</td>
                    <td className="col-tamanio">
                      {formatBytes(u.usedBytes)} / {formatBytes(u.quotaBytes)}
                    </td>
                    <td className="col-tamanio">
                      <span className={u.active ? 'chip activo' : 'chip inactivo'}>
                        {u.active ? 'Activo' : 'Baja'}
                      </span>
                    </td>
                    <td className="col-acciones">
                      <MenuAcciones
                        etiqueta={`Acciones de ${u.displayName}`}
                        opciones={[
                          { etiqueta: 'Editar…', onSelect: () => setEditando(u) },
                          {
                            etiqueta: u.active ? 'Dar de baja' : 'Reactivar',
                            onSelect: () => alternarActivo(u),
                            oculto: u.id === usuarioActual,
                          },
                          {
                            etiqueta: 'Eliminar cuenta',
                            onSelect: () => setPorEliminar({ u, purgar: false }),
                            peligro: true,
                            oculto: u.id === usuarioActual,
                          },
                          {
                            etiqueta: 'Eliminar cuenta y archivos',
                            onSelect: () => setPorEliminar({ u, purgar: true }),
                            peligro: true,
                            oculto: u.id === usuarioActual,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {resumen && resumen.actividad.length > 0 && (
            <>
              <div className="seccion-encabezado">
                <h2>Actividad reciente</h2>
              </div>
              <ul className="actividad">
                {resumen.actividad.map((a, i) => (
                  <li key={i}>
                    <span className="accion">{etiquetaAccion(a.action)}</span>
                    <span className="actor">{a.actor ?? 'anónimo'}</span>
                    <span className="objetivo">{a.target ?? ''}</span>
                    <span className="fecha">{formatDate(a.createdAt)}</span>
                  </li>
                ))}
              </ul>
              <p className="enlace-auditoria">
                <button className="link" onClick={() => setPestania('auditoria')}>
                  Ver toda la auditoría
                </button>
              </p>
            </>
          )}
        </>
      )}

      {porEliminar && (
        <Confirmar
          titulo={porEliminar.purgar ? 'Eliminar cuenta y archivos' : 'Eliminar cuenta'}
          mensaje={
            porEliminar.purgar
              ? `Se elimina la cuenta de ${porEliminar.u.displayName} y se borran sus ${formatBytes(porEliminar.u.usedBytes)} de archivos. No se puede deshacer.`
              : `Se elimina la cuenta de ${porEliminar.u.displayName}. Sus archivos se conservan en el storage.`
          }
          etiquetaAceptar="Eliminar"
          peligro
          onCancelar={() => setPorEliminar(null)}
          onAceptar={() => eliminar(porEliminar.u, porEliminar.purgar)}
        />
      )}

      {creando && (
        <UsuarioDialog
          onClose={() => setCreando(false)}
          onListo={() => {
            setCreando(false);
            void cargar();
          }}
        />
      )}

      {editando && (
        <UsuarioDialog
          usuario={editando}
          onClose={() => setEditando(null)}
          onListo={() => {
            setEditando(null);
            void cargar();
          }}
        />
      )}
    </section>
  );
}

function Tarjeta({
  titulo,
  valor,
  children,
}: {
  titulo: string;
  valor: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="tarjeta-resumen">
      <span className="titulo">{titulo}</span>
      <strong className="valor">{valor}</strong>
      {children && <div className="pie">{children}</div>}
    </div>
  );
}

function UsuarioDialog({
  usuario,
  onClose,
  onListo,
}: {
  usuario?: AdminUser;
  onClose: () => void;
  onListo: () => void;
}) {
  const esNuevo = !usuario;
  const [username, setUsername] = useState(usuario?.username ?? '');
  const [displayName, setDisplayName] = useState(usuario?.displayName ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(usuario?.role ?? 'user');
  const [cuotaGb, setCuotaGb] = useState(
    usuario ? Math.round((Number(usuario.quotaBytes) / GB) * 10) / 10 : 5,
  );
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function guardar() {
    setEnviando(true);
    setError(null);

    try {
      if (esNuevo) {
        await api.adminCreateUser({
          username: username.trim().toLowerCase(),
          displayName: displayName.trim(),
          email: email.trim() || null,
          password,
          role,
          quotaBytes: String(Math.round(cuotaGb * GB)),
        });
      } else {
        await api.adminUpdateUser(usuario.id, {
          displayName: displayName.trim(),
          email: email.trim() || null,
          role,
          quotaBytes: String(Math.round(cuotaGb * GB)),
          ...(password ? { password } : {}),
        });
      }
      onListo();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos guardar.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={esNuevo ? 'Nuevo usuario' : 'Editar usuario'}
      subtitulo={usuario?.username}
      onClose={onClose}
      acciones={
        <>
          <button className="secundario" onClick={onClose}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      {esNuevo && (
        <Campo id="nuevo-username" etiqueta="Usuario" ayuda="Minúsculas, números, punto y guion.">
          <input
            id="nuevo-username"
            value={username}
            autoFocus
            onChange={(e) => setUsername(e.target.value)}
          />
        </Campo>
      )}

      <Campo id="nuevo-nombre-visible" etiqueta="Nombre y apellido">
        <input
          id="nuevo-nombre-visible"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </Campo>

      <Campo id="nuevo-email" etiqueta="Correo" ayuda="Se usa para avisar de archivos compartidos.">
        <input id="nuevo-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Campo>

      <Campo
        id="nueva-clave"
        etiqueta={esNuevo ? 'Contraseña' : 'Nueva contraseña'}
        ayuda={esNuevo ? 'Mínimo 8 caracteres.' : 'Dejala vacía para no cambiarla. Cierra sus sesiones abiertas.'}
      >
        <input
          id="nueva-clave"
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
        />
      </Campo>

      <Campo id="nuevo-rol" etiqueta="Rol">
        <select id="nuevo-rol" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.nombre} — {r.detalle}
            </option>
          ))}
        </select>
      </Campo>

      <Campo id="nueva-cuota" etiqueta="Cuota (GB)">
        <input
          id="nueva-cuota"
          type="number"
          min={0.1}
          step={0.5}
          value={cuotaGb}
          onChange={(e) => setCuotaGb(Number(e.target.value))}
        />
      </Campo>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
